/**
 * Audio asset helpers and a small browser-based audio manager.
 *
 * This module does not include any audio files. Place assets under
 * `public/assets/audio/` and source them as documented below. The app will
 * load them on demand and gracefully skip playback if a file is missing.
 *
 * Expected asset layout (user-provided assets):
 *   public/assets/audio/
 *     move/          - move usage sounds (one per move, optional)
 *     hit/           - damage/impact sounds
 *     faint/         - Pokemon fainting sounds
 *     switch/        - Pokemon switch-in sounds
 *     ui/            - button clicks, toasts, chat, etc.
 *     battle/        - battle start, victory, defeat, turn tick
 *     music/         - optional background music loops
 *
 * Naming conventions are documented in `public/assets/audio/README.md`.
 */

export type AudioCategory =
  | "move"
  | "hit"
  | "faint"
  | "switch"
  | "ui"
  | "battle"
  | "music";

export type SoundEffect =
  | { type: "move"; move: string }
  | { type: "hit" }
  | { type: "faint" }
  | { type: "switch" }
  | { type: "ui"; sound: "click" | "toast" | "chat" | "error" }
  | { type: "battle"; sound: "start" | "victory" | "defeat" | "tick" };

const AUDIO_BASE = "/assets/audio";

/** Build a URL for an audio file in a given category. */
export function audioUrl(category: AudioCategory, filename: string): string {
  return `${AUDIO_BASE}/${category}/${filename}`;
}

/** Resolve a sound effect to a list of candidate URLs to try. */
export function resolveSoundEffect(effect: SoundEffect): string[] {
  switch (effect.type) {
    case "move":
      return [audioUrl("move", `${effect.move}.mp3`), audioUrl("move", `${effect.move}.ogg`)];
    case "hit":
      return [audioUrl("hit", "hit.mp3"), audioUrl("hit", "hit.ogg")];
    case "faint":
      return [audioUrl("faint", "faint.mp3"), audioUrl("faint", "faint.ogg")];
    case "switch":
      return [audioUrl("switch", "switch.mp3"), audioUrl("switch", "switch.ogg")];
    case "ui":
      return [audioUrl("ui", `${effect.sound}.mp3`), audioUrl("ui", `${effect.sound}.ogg`)];
    case "battle":
      return [audioUrl("battle", `${effect.sound}.mp3`), audioUrl("battle", `${effect.sound}.ogg`)];
    default:
      return [];
  }
}

/** Audio manager with lazy loading and mute/volume controls. */
export class AudioManager {
  private cache = new Map<string, HTMLAudioElement>();
  private enabled = true;
  private volume = 0.5;
  private music: HTMLAudioElement | null = null;

  isEnabled(): boolean {
    return this.enabled;
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) {
      this.stopMusic();
    }
  }

  getVolume(): number {
    return this.volume;
  }

  setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
    for (const audio of this.cache.values()) {
      audio.volume = this.volume;
    }
    if (this.music) {
      this.music.volume = this.volume;
    }
  }

  /** Play a one-shot sound effect. Missing files are silently ignored. */
  async playEffect(effect: SoundEffect): Promise<void> {
    if (!this.enabled) return;
    const urls = resolveSoundEffect(effect);
    for (const url of urls) {
      try {
        const audio = await this.loadAudio(url);
        audio.currentTime = 0;
        audio.volume = this.volume;
        await audio.play();
        return;
      } catch {
        // try next format or give up
      }
    }
  }

  /** Start background music if available. */
  async playMusic(track: string): Promise<void> {
    if (!this.enabled) return;
    this.stopMusic();
    const urls = [audioUrl("music", `${track}.mp3`), audioUrl("music", `${track}.ogg`)];
    for (const url of urls) {
      try {
        const audio = await this.loadAudio(url);
        audio.loop = true;
        audio.volume = this.volume;
        this.music = audio;
        await audio.play();
        return;
      } catch {
        // try next format or give up
      }
    }
  }

  stopMusic(): void {
    if (this.music) {
      this.music.pause();
      this.music.currentTime = 0;
      this.music = null;
    }
  }

  private loadAudio(url: string): Promise<HTMLAudioElement> {
    const cached = this.cache.get(url);
    if (cached) return Promise.resolve(cached);
    return new Promise((resolve, reject) => {
      const audio = new Audio(url);
      audio.oncanplaythrough = () => {
        this.cache.set(url, audio);
        resolve(audio);
      };
      audio.onerror = () => {
        console.warn(`[AudioManager] failed to load audio: ${url}`);
        reject(new Error(`Failed to load audio: ${url}`));
      };
    });
  }
}

/** Shared app-wide audio manager instance. */
export const audioManager = new AudioManager();
